import fitPrintTable from "@ext/markdown/elements/table/print/fitPrintTable";
import { throwIfAborted } from "@ext/print/utils/pagination/abort";
import type { PrintNodeHandler } from "@ext/print/utils/pagination/nodeHandlers";
import Paginator from "@ext/print/utils/pagination/Paginator";

/**
 * Every table this node carries, deepest first.
 *
 * A whole subtree and not the node alone, because a node that fits is never descended into: `tryFitElement`
 * appends it entire and `_handleNode` returns. A handler that only matched `data-component="table"` would
 * therefore never see a table inside tabs, inside a note, or inside another table's cell — those blocks are
 * placed whole far more often than not.
 *
 * Deepest first so an outer table measures its own narrowest width against an inner table that has already
 * been scaled, rather than against the width the inner one would have had unscaled.
 */
const depthOf = (node: Element): number => {
	let depth = 0;
	for (let parent = node.parentElement; parent; parent = parent.parentElement) depth++;
	return depth;
};

const tablesIn = (node: HTMLElement): HTMLElement[] => {
	const found = [...node.querySelectorAll<HTMLElement>('[data-component="table"]')];
	if (node.dataset?.component === "table") found.push(node);
	return found.sort((a, b) => depthOf(b) - depthOf(a));
};

/**
 * Fits every table under this node to the page before anything decides whether the node fits.
 *
 * `isRequired` and not a conditional handler: `Paginator._handleNode` runs the required list, then
 * `processNodeForPage`, then the conditional list. A conditional preparer is unreachable for exactly the
 * table that needs no splitting — `tryFitElement` places it whole and returns before the conditional list is
 * ever read — so a short wide table would print squeezed into single letters.
 *
 * Registered after the OpenAPI preparer, which is what turns a description's scroll box into a
 * `data-component="table"` wrapper in the first place (see openApi/print/descriptionBlocks.ts).
 *
 * Never claims the node; `tableHandler` still does the splitting.
 */
const tablePrepareFn: PrintNodeHandler["handle"] = async (node) => {
	const tables = tablesIn(node);
	if (!tables.length) return false;

	let scaled = false;
	for (const table of tables) scaled = fitPrintTable(table) || scaled;
	// Nothing was scaled — every height taken before this still holds, and a full subtree re-measure of a
	// spec's worth of nodes is not free.
	if (!scaled) return false;

	// Heights were taken before any of this, and a scaled table reports the size it would have had unscaled.
	await Paginator.paginationInfo.nodeDimension.remeasure(node, Paginator.controlInfo.yieldTick, () =>
		throwIfAborted(Paginator.controlInfo.signal),
	);

	return false;
};

const tablePrepareHandler: PrintNodeHandler = { isRequired: true, handle: tablePrepareFn };

export default tablePrepareHandler;
